/**
 * CJdropshipping vs Zendrop 子页面 — 页面常量（全量文案）
 * 路由：/cjdropshipping-vs-zendrop
 * 文案来源：docs/CJdropshipping vs 竞品专页面/CJ VS ZENDROP.html
 *
 * 【注意】
 * - VS_HERO.heroImage 为 Zendrop 专属 Unsplash 图，各竞品子页不同
 * - 图片链接目前使用 HTML 中的原链接，后续会迁移到 OSS
 * - 头像图片已从 pravatar.cc 迁移到 CDN（static-resource-platform/resources/egg/alternatives/）
 * - VS_TABLE.rows 对应表格数据从 HTML 提取，与 xlsx zendrop 子表保持一致
 * - TESTIMONIALS/SELLPOINTS/FIT/ADVANTAGES/BOTTOM_CTA 与主页相同，走公共组件
 */

/**
 * 【纠正】图片改用 CDN 路径（resources/egg/alternatives/），函数接受 cdnHost 参数。
 */
export function getZendropConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    // ========== VS_HERO: 子页 Banner ==========
    VS_HERO: {
      eyebrow: 'Zendrop Alternative',
      title: 'CJdropshipping vs',
      titleHighlight: 'ZenDrop',
      subtitle: 'A Better ZenDrop Alternative for Serious Dropshippers',
      desc: 'If you are looking for more than a crosslisting tool, CJdropshipping is a stronger ZenDrop alternative for sellers who want reliable sourcing, faster fulfillment, global warehouses, and branding support.',
      proofItems: ['Reliable sourcing', 'Faster fulfillment', 'Branding support'],
      ctaText: 'Try for free',
      ctaUrl: '/home',
      note: 'Built for sellers ready to scale beyond product listing.',
      // Zendrop 子页专属图片（尓商山地工作场景）
      heroImage: img('zendrop_hero.jpg'),
      topChipText: 'All-in-one growth support',
      bottomCard: {
        label: 'Why CJdropshipping',
        title: 'Beyond crosslisting, built for real growth',
        desc: 'Reliable sourcing, faster fulfillment, global warehouses, and branding support — all in one workflow.',
      },
    },

    // ========== SELLPOINTS: 卖点标签（SellpointsBar 直接 map 的 string[]） ==========
    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    // ========== VS_TABLE: 三列对比表 ==========
    VS_TABLE: {
      kicker: 'Platform Comparison',
      title: '<strong>CJdropshipping</strong> vs ZenDrop Comparison',
      desc: 'CJdropshipping offers a more complete dropshipping solution, covering product sourcing, quality inspection, warehousing, fulfillment, global shipping, and branding support.',
      summaryCards: [
        {
          icon: '1',
          title: 'More than listing',
          desc: 'ZenDrop helps with crosslisting, while CJdropshipping supports the full dropshipping workflow from sourcing to fulfillment.',
        },
        {
          icon: '2',
          title: 'Built for operations',
          desc: 'CJdropshipping gives sellers sourcing, inspection, warehousing, shipping, POD, and packaging support in one platform.',
        },
        {
          icon: '3',
          title: 'Better for scaling',
          desc: 'CJdropshipping is stronger for sellers who want long-term growth, brand scaling, and a better customer experience.',
        },
      ],
      headerFeature: 'Feature',
      headerCompetitor: 'ZenDrop',
      headerCj: 'CJdropshipping',
      rows: [
        {
          feature: 'Core Focus',
          competitor: 'Crosslisting and listing management',
          cj: 'Full dropshipping sourcing and fulfillment platform',
        },
        {
          feature: 'Best For',
          competitor: 'Marketplace resellers who need faster product listing',
          cj: 'Dropshippers who want to source, fulfill, ship, and scale',
        },
        {
          feature: 'Product Sourcing',
          competitor: 'Mainly depends on sellers\' existing suppliers',
          cj: 'Helps sellers source products and find better suppliers',
        },
        {
          feature: 'Supplier Support',
          competitor: 'Limited supplier-side support',
          cj: 'Offers sourcing agents and supplier resources',
        },
        {
          feature: 'Quality Inspection',
          competitor: 'Not a core feature',
          cj: 'Supports product and package inspection before shipping',
        },
        {
          feature: 'Warehousing',
          competitor: 'Limited',
          cj: 'Provides warehouse and inventory support',
        },
        {
          feature: 'Order Fulfillment',
          competitor: 'Not the main focus',
          cj: 'Handles order processing, packing, and fulfillment',
        },
        {
          feature: 'Global Shipping',
          competitor: 'Relies more on external supplier/shipping workflows',
          cj: 'Supports global logistics and shipping solutions',
        },
        {
          feature: 'Custom Packaging',
          competitor: 'Limited branding support',
          cj: 'Supports custom packaging and branded unboxing experiences',
        },
        {
          feature: 'Print on Demand',
          competitor: 'Limited',
          cj: 'Supports POD products and customization',
        },
        {
          feature: 'Store Integration',
          competitor: 'Focuses on marketplace listing workflows',
          cj: 'Supports major ecommerce platforms and order fulfillment workflows',
        },
        {
          feature: 'Scalability',
          competitor: 'Better for early-stage listing and reselling',
          cj: 'Better for long-term dropshipping growth and brand scaling',
        },
        {
          feature: 'Customer Experience',
          competitor: 'Helps publish products faster',
          cj: 'Helps improve delivery, product quality, and brand experience',
        },
      ],
      bottomText:
        'If you only need crosslisting, ZenDrop may help. But if you want sourcing, fulfillment, quality control, global shipping, packaging, and brand growth, CJdropshipping is the stronger long-term choice.',
      bottomCtaText: 'Try CJdropshipping',
      bottomCtaUrl: '/home',
    },

    // ========== FIT: Who Should Choose（由公共 FitSection 组件读取） ==========
    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Growth Sellers',
      floatTitle: 'Brand-building ecommerce sellers',
      floatDesc: 'A stronger fit for merchants who want sourcing, fulfillment, branding, and long-term operational support.',
      items: [
        'Need reliable product sourcing',
        'Want fulfillment support for dropshipping orders',
        'Sell through Shopify, WooCommerce, TikTok Shop, or eBay',
        'Want to test or sell POD products',
        'Need custom packaging or branding services',
        'Want to improve shipping and customer experience',
        'Plan to move from marketplace reselling to brand-based ecommerce',
        'See dropshipping as a long-term business, not just short-term product testing',
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond simple product copying. It helps you build a stronger business foundation through sourcing, fulfillment, customization, and branding support.',
    },

    // ========== ADVANTAGES: 核心优势（由公共 AdvantagesSection 组件读取） ==========
    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping stands out because it gives sellers the operational support needed to source better, fulfill faster, brand professionally, and scale with more confidence.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    // ========== TESTIMONIALS: 买家评价（与主页相同，走公共 TestimonialSection） ==========
    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      rows: [
        [
          {
            avatarUrl: img('32.jpeg'),
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            text: 'CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week.',
          },
          {
            avatarUrl: img('12.jpeg'),
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            text: 'As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business.',
          },
          {
            avatarUrl: img('47.jpeg'),
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            text: 'One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store.',
          },
        ],
        [
          {
            avatarUrl: img('15.jpeg'),
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            text: 'I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic.',
          },
          {
            avatarUrl: img('5.jpeg'),
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            text: 'Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work.',
          },
          {
            avatarUrl: img('60.jpeg'),
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            text: 'CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience.',
          },
        ],
      ],
    },

    // ========== VS_FAQ: 子页专属 FAQ（左侧摘要卡片布局） ==========
    VS_FAQ: {
      kicker: 'FAQ',
      title: 'ZenDrop vs <strong>CJdropshipping</strong> FAQ',
      intro:
        'Get quick answers about how CJdropshipping compares with ZenDrop for sourcing, fulfillment, shipping, branding, and long-term dropshipping growth.',
      sideCard: {
        label: 'Quick Decision Guide',
        title: 'Need more than a crosslisting tool?',
        desc: 'ZenDrop is useful for listing workflows, while CJdropshipping gives sellers more support across sourcing, fulfillment, logistics, packaging, and brand growth.',
        miniItems: [
          { text: 'Better for sourcing and fulfillment' },
          { text: 'Supports global shipping and warehousing' },
          { text: 'Helps sellers build branded customer experiences' },
        ],
      },
      items: [
        {
          q: 'Is CJdropshipping better than ZenDrop?',
          a: 'CJdropshipping is better for sellers who need product sourcing, fulfillment, quality inspection, global shipping, warehousing, and branding support. ZenDrop is better for sellers who mainly need crosslisting and product listing tools.',
        },
        {
          q: 'What is the best ZenDrop alternative?',
          a: 'CJdropshipping is a strong ZenDrop alternative for dropshippers who want more than listing automation. It supports product sourcing, order fulfillment, global logistics, custom packaging, and brand-building services.',
        },
        {
          q: 'Can CJdropshipping replace ZenDrop?',
          a: 'CJdropshipping can replace ZenDrop if your main goal is sourcing and fulfilling dropshipping products. If your main goal is crosslisting across many marketplaces, ZenDrop may still be useful. For supply chain and fulfillment, CJdropshipping is the stronger option.',
        },
        {
          q: 'Is ZenDrop a supplier?',
          a: 'ZenDrop is primarily a crosslisting and product management tool. It helps sellers list and monitor products, but it is not mainly positioned as a full sourcing, warehouse, and fulfillment supplier platform.',
        },
        {
          q: 'Does CJdropshipping support custom packaging?',
          a: 'Yes. CJdropshipping supports custom packaging options that help sellers create branded unboxing experiences.',
        },
        {
          q: 'Which is better for Shopify dropshipping, CJdropshipping or ZenDrop?',
          a: 'CJdropshipping is usually the better fit for Shopify sellers who need product sourcing, fulfillment, shipping, and branding. ZenDrop is more useful for crosslisting and marketplace-oriented workflows.',
        },
        {
          q: 'Which is better for beginners?',
          a: 'ZenDrop may be easier for beginners who only want to crosslist products quickly. CJdropshipping is better for beginners who want to build a more complete dropshipping operation from sourcing to delivery.',
        },
      ],
      bottomText: 'Still have questions? Start with a free account and explore what CJdropshipping can do for your business.',
      bottomCtaText: 'Try CJdropshipping Free',
      bottomCtaUrl: '/home',
    },

    // ========== BOTTOM_CTA: 底部行动召唤（由公共 BottomCta 组件读取） ==========
    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Need More Than a <strong>Crosslisting Tool?</strong><br>Try CJdropshipping',
      desc: 'CJdropshipping helps you source, fulfill, ship, and brand your dropshipping products — all in one platform.',
      ctaText: 'Explore CJ Service',
      ctaUrl: '/home',
      // 【纠正】补充背景图，否则 BottomCta 组件 backgroundImage 内联样式为空，背景无法显示
      bgImage: img('hero_banner.jpg'),
    },
  };
}
